import { Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SoundButton } from "components/buttons/SoundButton";
import classes from "./stripChatVideo.module.scss";
import MessageIcon from "components/icons/message.svg?react";
import DefaultUserPicIcon from "components/icons/default_userpic.svg?react";
import { isDisableAds } from "components/ads/utils";
import { useExperiments } from "hooks/useExperiments";
import { className } from "utils";
import { HlsPlayer } from "components/UI/HlsPlayer";
import { isSupportNativeHls } from "../../player/Video/utils/hls";

interface IProps {
  username?: string;
  avatarUrl?: string;
  videoUrl?: string;
  previewUrl?: string;
  width?: number;
  height?: number;
}

const StripChatVideo = ({ username, avatarUrl, previewUrl, videoUrl, width = 0, height = 0 }: IProps) => {
  const [isSound, setSound] = useState(false);
  const [isVisible, setVisible] = useState(false);
  const [canPlayHls, setCanPlayHls] = useState(isSupportNativeHls);
  const videoRef = useRef<HTMLVideoElement>(null);

  const experiments = useExperiments();

  const isRotate = experiments.rotate && width > height;

  const isHls = useMemo(() => {
    const url = videoUrl || "";
    return url.substring(url.lastIndexOf(".")) === ".m3u8";
  }, [videoUrl]);

  const ref = useRef<HTMLDivElement | null>(null);

  const visibleObserver = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, {
      threshold: 0.1
    });
  }, []);

  const handleToggleSound = useCallback(() => {
    setSound(!isSound);
  }, [isSound]);

  const handleClick = useCallback(() => {
    const domain = import.meta.env.REACT_APP_STRIPCHAT_URL ?? "/";

    const params = new URLSearchParams({
      campaignId: "InFeed",
      userId: import.meta.env.REACT_APP_STRIPCHAT_USER_ID ?? "",
      targetDomain: import.meta.env.REACT_APP_STRIPCHAT_TARGET_DOMAIN ?? "",
      onlineModels: username ?? ""
    });

    window.location.href = `${domain}/?${params.toString()}`;
  }, [username]);

  const handleError = useCallback((e: never) => {
    const v = videoRef.current;

    if (v && v.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED && isHls && canPlayHls) {
      setCanPlayHls(false);
    }
  }, [videoRef, isHls, canPlayHls]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    visibleObserver.observe(ref.current);

    return () => {
      visibleObserver.disconnect();
    };
  }, [ref, visibleObserver]);

  return (
    <Fragment>
      {!isDisableAds() ?
        <div
          ref={ref}
          className={classes.stripChatVideo}
        >
          <div className={classes.backdropContainer}>
            { previewUrl && <img className={classes.backdrop} src={previewUrl} alt="Poster for strip chat"/> }
          </div>

          { isVisible && (
            <div
              data-videoads="adsVideo"
              className={className({
                [classes.playerContainer]: true,
                [classes.isRotate]: isRotate,
              })}
              onClick={handleClick}
            >
              { videoUrl && (
                <Fragment>
                  {
                    isHls && !canPlayHls ? (
                      <Suspense>
                        <HlsPlayer
                          src={videoUrl}
                          autoPlay={true}
                          controls={false}
                          muted={!isSound}
                          playsInline={true}
                          disablePictureInPicture={true}
                          isHls={isHls}
                        />
                      </Suspense>
                    ) : (
                      <video
                        src={videoUrl}
                        ref={videoRef}
                        autoPlay={true}
                        controls={false}
                        muted={!isSound}
                        playsInline={true}
                        disablePictureInPicture={true}
                        onError={handleError}
                      />
                    )
                  }
                </Fragment>
              )}

              <div className={classes.overlay} />
            </div>
          )}

          <div className={classes.controls}>
            <SoundButton
              enabled={isSound}
              onClick={handleToggleSound}
            />
          </div>

          <div className={classes.modelInfo} onClick={handleClick}>
            <div className={classes.callToActions}>
              <div className={classes.callToActionsSeparator} />
              <div className={classes.callToActionsText}>
                <MessageIcon/>
                <span>Tap to chat</span>
              </div>
              <div className={classes.callToActionsSeparator} />
            </div>

            <div className={classes.model}>
              <div className={classes.modelAvatarContainer}>
                {
                  avatarUrl && avatarUrl.length > 0
                    ? <img className={classes.modelAvatar} src={avatarUrl} alt={`Poster for ${username ?? "strip chat model"}`}/>
                    : <DefaultUserPicIcon className={classes.modelAvatar} />
                }
              </div>
              <div className={classes.modelNameContainer}>
                <div className={classes.modelName}>{username ?? ""}</div>
              </div>
            </div>
          </div>
        </div>
        : null }
    </Fragment>
  );
};

export default StripChatVideo;
